import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createSimpleStore } from 'react-simple-reducer'
import { CupomModel } from '../../models/CupomModel'
import { toast } from 'react-toastify'
import axios from '../../api'
import { useHistory, useParams } from 'react-router-dom'
import {
  Switch,
  DatePicker,
  Input,
  Tabs,
  notification,
  Select,
  Checkbox,
  InputRef,
  InputProps,
  Button,
  Card,
} from 'antd'
import styled from '@emotion/styled'
import ptBR from 'antd/es/date-picker/locale/pt_BR'
import dayjs from 'dayjs'
import { ParceirosModel } from '../../models/ParceirosModel'
import { CursoModel } from '../../models/CursoModel'
import Inputmask from 'inputmask'
import { ArrowLeftOutlined } from '@ant-design/icons'

const { TabPane } = Tabs

const ufs = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'] // prettier-ignore

function normalizeCourseIds(cursos: unknown): number[] {
  if (!Array.isArray(cursos)) return []
  return Array.from(
    new Set(cursos.map((cursoId) => Number(cursoId)).filter((cursoId) => Number.isFinite(cursoId)))
  )
}

export const CuponsEditar = () => {
  const params = useParams<{ cupomId?: string }>()

  const init = useCallback(
    (dispatch) => {
      if (params.cupomId !== 'novo') {
        dispatch(Store.thunks.getCupom(Number(params.cupomId)))
      }
    },
    [params.cupomId]
  )

  return (
    <Store.Provider init={init}>
      <Layout>
        <CuponsEditarHeader />
        <EditorCard>
          <ContentShell>
            <InputSizingStyles>
              <EditorStack>
                <Codigo />
                <Vigencia />
                <Desconto />
                <Parceiro />
                <Ufs />
                <Cursos />
              </EditorStack>
              <Salvar />
            </InputSizingStyles>
          </ContentShell>
        </EditorCard>
      </Layout>
    </Store.Provider>
  )
}

const CuponsEditarHeader = () => {
  const history = useHistory()
  const params = useParams<{ cupomId?: string }>()
  const pageTitle = params.cupomId === 'novo' ? 'Novo cupom' : `Cupom #${params.cupomId}`

  return (
    <PageHeader>
      <div>
        <Title>{pageTitle}</Title>
      </div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/cupons')}>
        Voltar
      </Button>
    </PageHeader>
  )
}

const EditorSection = ({
  title,
  className,
  children,
}: {
  title: string
  className?: string
  children: React.ReactNode
}) => (
  <SectionCard className={className}>
    <SectionHeader>
      <SectionTitle>{title}</SectionTitle>
    </SectionHeader>
    {children}
  </SectionCard>
)

function Codigo() {
  const { cupom } = Store.useState()
  const dispatch = Store.useDispatch()

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    dispatch(Store.actions.changeCodigo(e.target.value))
  }

  return (
    <EditorSection title="Código do cupom">
      <Input
        id="cupom-codigo"
        value={cupom.codigo}
        onChange={handleChange}
        size="large"
        autoComplete="off"
        placeholder="Ex: DESCONTO10"
      />
    </EditorSection>
  )
}

function Vigencia() {
  const { cupom } = Store.useState()
  const dispatch = Store.useDispatch()

  const handleDateRangeChange = (dates: [dayjs.Dayjs, dayjs.Dayjs] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      dispatch(Store.actions.changeVigenciaInicio(null))
      dispatch(Store.actions.changeVigenciaFim(null))
      return
    }

    const [startDate, endDate] = dates
    dispatch(Store.actions.changeVigenciaInicio(startDate.startOf('day').toISOString()))
    dispatch(Store.actions.changeVigenciaFim(endDate.endOf('day').toISOString()))
  }

  return (
    <EditorSection title="Vigência">
      <DatePicker.RangePicker
        size="large"
        onChange={handleDateRangeChange}
        value={[
          cupom.dataInicioVigencia ? dayjs(cupom.dataInicioVigencia) : null,
          cupom.dataFimVigencia ? dayjs(cupom.dataFimVigencia) : null,
        ]}
        locale={ptBR}
        format="DD/MM/YYYY"
        placeholder={['Data início', 'Data fim']}
        style={{ width: '100%' }}
      />
    </EditorSection>
  )
}

function Desconto() {
  const {
    tipoDesconto,
    cupom: { valorDesconto, percentualDesconto },
  } = Store.useState()
  const dispatch = Store.useDispatch()

  return (
    <EditorSection title="Desconto">
      <Tabs
        activeKey={tipoDesconto}
        onChange={(e) => dispatch(Store.actions.changeTipoDesconto(e))}
      >
        <TabPane tab="Preço fixo" key="fixed">
          <FieldLabel>Valor do desconto</FieldLabel>
          <CompactControl>
            <BrazilianNumberInput
              value={valorDesconto}
              style={{ width: '100%' }}
              size="large"
              prefix="R$ "
              onChange={(value) => dispatch(Store.actions.changeValorDesconto(value))}
              placeholder="0,00"
            />
          </CompactControl>
        </TabPane>
        <TabPane tab="Percentual" key="percent">
          <FieldLabel>Percentual do desconto</FieldLabel>
          <CompactControl>
            <BrazilianNumberInput
              style={{ width: '100%' }}
              size="large"
              suffix="%"
              value={percentualDesconto}
              onChange={(value) => dispatch(Store.actions.changePercentualDesconto(value))}
              placeholder="0,00"
            />
          </CompactControl>
        </TabPane>
      </Tabs>
    </EditorSection>
  )
}

function Parceiro() {
  const {
    hasParceiro,
    tipoComissao,
    cupom: { valorComissao, percentualComissao },
  } = Store.useState()
  const dispatch = Store.useDispatch()

  return (
    <EditorSection title="Parceiro e comissão">
      <SwitchRow>
        <Switch
          checked={hasParceiro}
          onChange={() => dispatch(Store.actions.changeHasParceiro())}
        />
        <SwitchText>
          {hasParceiro
            ? 'Parceiro habilitado para esse cupom'
            : 'Marque para informar parceiro e comissão'}
        </SwitchText>
      </SwitchRow>

      {hasParceiro ? (
        <>
          <SelectParceiro />
          <Tabs
            size="small"
            activeKey={tipoComissao}
            onChange={(e) => dispatch(Store.actions.changeTipoComissao(e))}
          >
            <TabPane tab="Comissão fixa" key="fixed">
              <FieldLabel>Valor da comissão</FieldLabel>
              <CompactControl>
                <BrazilianNumberInput
                  value={valorComissao}
                  onChange={(value) => dispatch(Store.actions.changeComissaoFixa(value))}
                  style={{ width: '100%' }}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </CompactControl>
            </TabPane>
            <TabPane tab="Comissão percentual" key="percent">
              <FieldLabel>Percentual da comissão</FieldLabel>
              <CompactControl>
                <BrazilianNumberInput
                  suffix="%"
                  value={percentualComissao}
                  onChange={(value) => dispatch(Store.actions.changeComissaoPercentual(value))}
                  style={{ width: '100%' }}
                  placeholder="0,00"
                />
              </CompactControl>
            </TabPane>
          </Tabs>
        </>
      ) : null}
    </EditorSection>
  )
}

function SelectParceiro() {
  const {
    parceiros,
    cupom: { parceiroId },
  } = Store.useState()
  const dispatch = Store.useDispatch()

  useEffect(() => {
    if (parceiros.length) return

    async function getParceiros() {
      try {
        const response = await axios.Parceiros.get()
        dispatch(Store.actions.getParceirosSuccess(response))
      } catch (error: any) {
        const message = error.response?.data?.message ?? 'Erro ao buscar os parceiros'
        notification.error({ message })
      }
    }

    void getParceiros()
  }, [dispatch, parceiros.length])

  return (
    <FieldStack>
      <FieldLabel>Parceiro</FieldLabel>
      <Select
        showSearch
        placeholder="Selecione o parceiro"
        size="large"
        style={{ width: '100%' }}
        onChange={(value) => dispatch(Store.actions.changeParceiro(value))}
        value={parceiroId}
        filterOption={(input, option) => {
          return (option.children as any).toLowerCase().indexOf(input.toLowerCase()) >= 0
        }}
      >
        {parceiros.map((parceiro) => (
          <Select.Option key={parceiro.id} value={parceiro.id}>
            {parceiro.nome}
          </Select.Option>
        ))}
      </Select>
    </FieldStack>
  )
}

function Ufs() {
  const { cupom } = Store.useState()
  const dispatch = Store.useDispatch()

  const handleSelectAll = () => {
    if (cupom.ufs?.length) {
      dispatch(Store.actions.changeUfs([]))
    } else {
      dispatch(Store.actions.changeUfs(ufs))
    }
  }

  return (
    <EditorSection title="UFs" className="full-width">
      <Checkbox
        onChange={handleSelectAll}
        indeterminate={cupom.ufs?.length >= 1 && cupom.ufs?.length < ufs.length}
        checked={cupom.ufs?.length === ufs.length}
      >
        Selecionar todas as UFs
      </Checkbox>
      <FieldStack>
        <Select
          mode="multiple"
          placeholder="Selecione as UFs"
          size="large"
          style={{ width: '100%' }}
          onChange={(value) => dispatch(Store.actions.changeUfs(value))}
          value={cupom.ufs}
        >
          {ufs.map((uf) => (
            <Select.Option key={uf} value={uf}>
              {uf}
            </Select.Option>
          ))}
        </Select>
      </FieldStack>
    </EditorSection>
  )
}

function Cursos() {
  const { cupom, cursos } = Store.useState()
  const dispatch = Store.useDispatch()

  useEffect(() => {
    if (cursos.length) return

    async function fetchCursos() {
      try {
        const response = await axios.Cursos.get()
        dispatch(Store.actions.getCursosSuccess(response))
      } catch (error) {
        console.error('Error fetching cursos:', error)
      }
    }

    void fetchCursos()
  }, [cursos.length, dispatch])

  const handleSelectAll = () => {
    if (cupom.cursos?.length) {
      dispatch(Store.actions.setCursos([]))
    } else {
      dispatch(Store.actions.setCursos(cursos.map((curso) => curso.id)))
    }
  }

  return (
    <EditorSection title="Cursos" className="full-width">
      <Checkbox
        onChange={handleSelectAll}
        indeterminate={cupom.cursos?.length >= 1 && cupom.cursos?.length < cursos.length}
        checked={cupom.cursos?.length === cursos.length}
      >
        Selecionar todos os cursos
      </Checkbox>
      <FieldStack>
        <Select
          mode="multiple"
          placeholder="Selecione os cursos"
          size="large"
          style={{ width: '100%', maxWidth: '100%' }}
          onChange={(value) => dispatch(Store.actions.setCursos(value))}
          value={normalizeCourseIds(cupom.cursos)}
          filterOption={(input, option) =>
            (option.children as any).toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {cursos.map((curso) => (
            <Select.Option key={curso.id} value={curso.id}>
              {curso.nomeReduzido || curso.nome}
            </Select.Option>
          ))}
        </Select>
      </FieldStack>
    </EditorSection>
  )
}

function Salvar() {
  const { cupom, hasParceiro, tipoComissao, tipoDesconto } = Store.useState()
  const dispatch = Store.useDispatch()
  const [loading, setLoading] = useState(false)

  async function handleClick() {
    const showErr = (message) => notification.error({ message })
    try {
      setLoading(true)
      const newCupom: CupomModel = { ...cupom }

      if (!newCupom.codigo) return showErr('Informe o código')

      if (tipoDesconto === 'fixed') newCupom.percentualDesconto = null
      if (tipoDesconto === 'percent') newCupom.valorDesconto = null
      if (newCupom.valorDesconto == null && newCupom.percentualDesconto == null) {
        return showErr('Informe o desconto')
      }

      if (hasParceiro) {
        if (tipoComissao === 'fixed') newCupom.percentualComissao = null
        if (tipoComissao === 'percent') newCupom.valorComissao = null

        if (newCupom.valorComissao == null && newCupom.percentualComissao == null) {
          return showErr('Informe o valor da comissão')
        }

        if (!newCupom.parceiroId) return showErr('Informe o parceiro')
      } else {
        newCupom.parceiroId = null
        newCupom.percentualComissao = null
        newCupom.valorComissao = null
      }

      if (!newCupom.dataInicioVigencia) newCupom.dataInicioVigencia = null
      if (!newCupom.dataFimVigencia) newCupom.dataFimVigencia = null
      newCupom.cursos = normalizeCourseIds(newCupom.cursos)

      const createdCupom = await axios.Cupons.post(newCupom)
      dispatch(Store.actions.createCupomSuccess(createdCupom))
      notification.success({ message: 'Cupom salvo com sucesso' })
    } catch (error: any) {
      const message = error.response?.data?.message ?? 'Erro ao salvar o cupom'
      notification.error({ message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ActionRow>
      <Button size="large" type="primary" onClick={handleClick} loading={loading}>
        Salvar
      </Button>
    </ActionRow>
  )
}

function BrazilianNumberInput({
  value,
  onChange,
  ...props
}: Omit<InputProps, 'value' | 'onChange'> & {
  value: number | null | undefined
  onChange: (value: number) => any
}) {
  const ref = useRef<InputRef>(null)
  const [currentValue, setCurrentValue] = useState(mask(value))

  useEffect(() => {
    if (ref.current) {
      Inputmask({
        alias: 'currency',
        groupSeparator: '.',
        radixPoint: ',',
        digits: 2,
        unmaskAsNumber: true,
      }).mask(ref.current.input)
    }
  }, [ref])

  useEffect(() => {
    if (unmask(currentValue) !== (value ?? 0)) {
      setCurrentValue(mask(value))
    }
  }, [currentValue, value])

  return (
    <Input
      value={currentValue}
      ref={ref}
      onChange={(e) => {
        const unmaskedValue = unmask(e.target.value)
        onChange(unmaskedValue)
        setCurrentValue(e.target.value)
      }}
      {...props}
    />
  )
}

function unmask(value: string) {
  return +(value ?? '').replace(/[R\$ \.]/g, '').replace(',', '.')
}

function mask(value: number | null | undefined) {
  if (value != null && value !== 0) {
    return `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(value)}`
  }
  return '0,00'
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const EditorCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }
`

const ContentShell = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 16px;
`

const EditorStack = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  .full-width {
    grid-column: 1 / -1;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;

    .full-width {
      grid-column: auto;
    }
  }
`

const SectionCard = styled.section`
  border: 1px solid #e9edf3;
  border-radius: 12px;
  background: #fff;
  padding: 16px;
  height: 100%;
`

const SectionHeader = styled.div`
  margin-bottom: 8px;
`

const SectionTitle = styled.h5`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 500;
`

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
`

const SwitchText = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
`

const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f0f0f0;
`

const FieldStack = styled.div`
  margin-top: 10px;

  .ant-select {
    width: 100%;
  }
`

const CompactControl = styled.div`
  width: 100%;
`

const InputSizingStyles = styled.div`
  .ant-input,
  .ant-input-affix-wrapper,
  .ant-picker,
  .ant-select-selector {
    min-height: 44px !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    border-radius: 10px !important;
  }

  .ant-picker-input > input,
  .ant-input {
    font-size: 16px;
  }
`

const Store = createSimpleStore(
  {
    hasParceiro: false,
    cupom: new CupomModel() as CupomModel,
    loading: false,
    parceiros: [] as ParceirosModel[],
    cursos: [] as CursoModel[],
    tipoComissao: 'fixed' as 'fixed' | 'percent',
    tipoDesconto: 'fixed' as 'fixed' | 'percent',
  },
  {
    getCupomStarted(state) {
      state.loading = true
    },
    getCupomSuccess(state, cupom: CupomModel) {
      state.cupom = { ...cupom, cursos: normalizeCourseIds(cupom.cursos) }
      state.loading = false
      state.hasParceiro = !!cupom.parceiroId
      if (cupom.percentualComissao) state.tipoComissao = 'percent'
      if (cupom.percentualDesconto) state.tipoDesconto = 'percent'
    },
    getCupomError(state) {
      state.loading = false
    },
    changeCodigo(state, codigo) {
      state.cupom = { ...state.cupom, codigo }
    },
    changeVigenciaInicio(state, data) {
      state.cupom = { ...state.cupom, dataInicioVigencia: data }
    },
    changeVigenciaFim(state, data) {
      state.cupom = { ...state.cupom, dataFimVigencia: data }
    },
    changePercentualDesconto(state, valor) {
      state.cupom = { ...state.cupom, percentualDesconto: valor }
    },
    changeValorDesconto(state, valor) {
      state.cupom = { ...state.cupom, valorDesconto: valor }
    },
    changeHasParceiro(state) {
      state.hasParceiro = !state.hasParceiro
    },
    changeComissaoFixa(state, valor) {
      state.cupom = { ...state.cupom, valorComissao: valor }
    },
    changeComissaoPercentual(state, valor) {
      state.cupom = { ...state.cupom, percentualComissao: valor }
    },
    getParceirosSuccess(state, parceiros: ParceirosModel[]) {
      state.parceiros = parceiros
    },
    changeParceiro(state, parceiroId) {
      state.cupom = { ...state.cupom, parceiroId }
    },
    changeUfs(state, ufs) {
      state.cupom = { ...state.cupom, ufs }
    },
    getCursosSuccess(state, cursos) {
      state.cursos = cursos.rows
    },
    setCursos(state, cursos) {
      state.cupom = { ...state.cupom, cursos: normalizeCourseIds(cursos) }
    },
    createCupomSuccess(state, cupom) {
      state.cupom = { ...cupom, cursos: normalizeCourseIds(cupom.cursos) }
    },
    changeTipoComissao(state, tipoComissao) {
      state.tipoComissao = tipoComissao
    },
    changeTipoDesconto(state, tipoDesconto) {
      state.tipoDesconto = tipoDesconto
    },
  },
  {
    thunks: {
      getCupom(cupomId: number) {
        return async (dispatch) => {
          try {
            dispatch(Store.actions.getCupomStarted())
            const cupom = await axios.Cupons.getById(cupomId)
            dispatch(Store.actions.getCupomSuccess(cupom))
          } catch (error: any) {
            toast(error.response?.data?.message ?? 'Ocorreu um erro')
            dispatch(Store.actions.getCupomError())
          }
        }
      },
    },
  }
)
